import type {
  SystemEvidenceRecord,
} from '@/governor/system-workspaces/SystemWorkspaceContracts';

export const CALLS_SIMULATOR_SCENARIOS = [
  'successful-call',
  'answer-timeout',
  'notification-delivery-failure',
  'duplicate-accept',
  'end-route-failure',
] as const;

export type CallsSimulatorScenarioId =
  typeof CALLS_SIMULATOR_SCENARIOS[number];

export type CallsSimulatorStage =
  | 'created'
  | 'ringing'
  | 'notified'
  | 'accepted'
  | 'connecting'
  | 'connected'
  | 'declined'
  | 'missed'
  | 'ended'
  | 'failed';

export type CallsSimulatorStep =
  Readonly<{
    sequence: number;
    stage: CallsSimulatorStage;
    actor:
      | 'caller'
      | 'receiver'
      | 'system';
    status:
      | 'pass'
      | 'warning'
      | 'fail';
    summary: string;
  }>;

export type CallsSimulatorResult =
  Readonly<{
    id: string;
    scenarioId:
      CallsSimulatorScenarioId;
    label: string;
    mode: 'SIMULATED';
    productionWritesAttempted: false;
    externalDeliveryAttempted: false;
    expectedFailure: boolean;
    outcome:
      | 'pass'
      | 'fail';
    startedAt: string;
    completedAt: string;
    steps:
      readonly CallsSimulatorStep[];
    evidence:
      readonly SystemEvidenceRecord[];
  }>;

type ScenarioDefinition =
  Readonly<{
    id:
      CallsSimulatorScenarioId;
    label: string;
    expectedFailure: boolean;
    steps:
      readonly Omit<
        CallsSimulatorStep,
        'sequence'
      >[];
  }>;

const scenarios:
  readonly ScenarioDefinition[] = [
    {
      id: 'successful-call',
      label:
        'Successful end-to-end lifecycle',
      expectedFailure: false,
      steps: [
        {
          stage: 'created',
          actor: 'caller',
          status: 'pass',
          summary:
            'Caller creates one governed call session.',
        },
        {
          stage: 'ringing',
          actor: 'system',
          status: 'pass',
          summary:
            'Lifecycle authority records ringing.',
        },
        {
          stage: 'notified',
          actor: 'receiver',
          status: 'pass',
          summary:
            'Receiver notification is simulated.',
        },
        {
          stage: 'accepted',
          actor: 'receiver',
          status: 'pass',
          summary:
            'Receiver accepts the call.',
        },
        {
          stage: 'connecting',
          actor: 'system',
          status: 'pass',
          summary:
            'WebRTC connection begins.',
        },
        {
          stage: 'connected',
          actor: 'system',
          status: 'pass',
          summary:
            'Peer media becomes connected.',
        },
        {
          stage: 'ended',
          actor: 'caller',
          status: 'pass',
          summary:
            'Call ends and cleanup completes.',
        },
      ],
    },
    {
      id: 'answer-timeout',
      label:
        'Receiver answer timeout',
      expectedFailure: true,
      steps: [
        {
          stage: 'created',
          actor: 'caller',
          status: 'pass',
          summary:
            'Caller creates one governed call session.',
        },
        {
          stage: 'ringing',
          actor: 'system',
          status: 'pass',
          summary:
            'Lifecycle authority records ringing.',
        },
        {
          stage: 'notified',
          actor: 'receiver',
          status: 'pass',
          summary:
            'Receiver notification is simulated.',
        },
        {
          stage: 'missed',
          actor: 'system',
          status: 'warning',
          summary:
            'The ringing timeout expires without an answer.',
        },
        {
          stage: 'ended',
          actor: 'system',
          status: 'pass',
          summary:
            'The simulated session is closed.',
        },
      ],
    },
    {
      id:
        'notification-delivery-failure',
      label:
        'Incoming notification delivery failure',
      expectedFailure: true,
      steps: [
        {
          stage: 'created',
          actor: 'caller',
          status: 'pass',
          summary:
            'Caller creates one governed call session.',
        },
        {
          stage: 'ringing',
          actor: 'system',
          status: 'pass',
          summary:
            'Lifecycle authority records ringing.',
        },
        {
          stage: 'failed',
          actor: 'system',
          status: 'fail',
          summary:
            'Simulated notification delivery fails.',
        },
        {
          stage: 'missed',
          actor: 'system',
          status: 'warning',
          summary:
            'The unanswered call becomes missed.',
        },
      ],
    },
    {
      id: 'duplicate-accept',
      label:
        'Duplicate answer suppression',
      expectedFailure: true,
      steps: [
        {
          stage: 'created',
          actor: 'caller',
          status: 'pass',
          summary:
            'Caller creates one governed call session.',
        },
        {
          stage: 'ringing',
          actor: 'system',
          status: 'pass',
          summary:
            'Lifecycle authority records ringing.',
        },
        {
          stage: 'accepted',
          actor: 'receiver',
          status: 'pass',
          summary:
            'The first answer succeeds.',
        },
        {
          stage: 'accepted',
          actor: 'receiver',
          status: 'warning',
          summary:
            'The repeated answer is suppressed.',
        },
        {
          stage: 'connecting',
          actor: 'system',
          status: 'pass',
          summary:
            'Only one connection proceeds.',
        },
      ],
    },
    {
      id: 'end-route-failure',
      label:
        'Post-call return-route failure',
      expectedFailure: true,
      steps: [
        {
          stage: 'created',
          actor: 'caller',
          status: 'pass',
          summary:
            'Caller creates one governed call session.',
        },
        {
          stage: 'ringing',
          actor: 'system',
          status: 'pass',
          summary:
            'Lifecycle authority records ringing.',
        },
        {
          stage: 'accepted',
          actor: 'receiver',
          status: 'pass',
          summary:
            'Receiver accepts the call.',
        },
        {
          stage: 'connecting',
          actor: 'system',
          status: 'pass',
          summary:
            'WebRTC connection begins.',
        },
        {
          stage: 'connected',
          actor: 'system',
          status: 'pass',
          summary:
            'Peer media becomes connected.',
        },
        {
          stage: 'ended',
          actor: 'system',
          status: 'pass',
          summary:
            'Lifecycle authority records ended.',
        },
        {
          stage: 'failed',
          actor: 'system',
          status: 'fail',
          summary:
            'Simulated return routing resolves incorrectly.',
        },
      ],
    },
  ];

export function listCallsSimulatorScenarios() {
  return scenarios.map(
    scenario =>
      Object.freeze({
        id: scenario.id,
        label: scenario.label,
        expectedFailure:
          scenario.expectedFailure,
      }),
  );
}

export function runCallsControlledSimulation(
  scenarioId:
    CallsSimulatorScenarioId,
  nowValue:
    string =
      new Date().toISOString(),
): CallsSimulatorResult {
  const scenario =
    scenarios.find(
      value =>
        value.id === scenarioId,
    );

  if (!scenario) {
    throw new Error(
      `Unknown Calls scenario: ${scenarioId}`,
    );
  }

  const runId =
    `calls-simulation:${scenarioId}:${nowValue}`;

  const steps =
    scenario.steps.map(
      (step, index) =>
        Object.freeze({
          ...step,
          sequence: index + 1,
        }),
    );

  const evidence =
    steps.map<SystemEvidenceRecord>(
      step =>
        Object.freeze({
          id:
            `${runId}:step:${step.sequence}`,
          systemId: 'calls',
          mode: 'SIMULATED',
          category:
            step.status === 'fail'
              ? 'incident'
              : 'simulation',
          source:
            'CallsControlledSimulator',
          summary: step.summary,
          observedAt: nowValue,
          critical:
            step.status === 'fail',
          passed:
            step.status === 'pass'
              ? true
              : step.status === 'fail'
                ? false
                : null,
          correlationId: runId,
          payload: Object.freeze({
            scenarioId,
            sequence:
              step.sequence,
            stage: step.stage,
            actor: step.actor,
            status: step.status,
            productionWrite: false,
            externalDelivery: false,
          }),
        }),
    );

  const unexpectedFailure =
    steps.some(
      step =>
        step.status === 'fail',
    ) &&
    !scenario.expectedFailure;

  return Object.freeze({
    id: runId,
    scenarioId,
    label: scenario.label,
    mode: 'SIMULATED',
    productionWritesAttempted: false,
    externalDeliveryAttempted: false,
    expectedFailure:
      scenario.expectedFailure,
    outcome:
      unexpectedFailure
        ? 'fail'
        : 'pass',
    startedAt: nowValue,
    completedAt: nowValue,
    steps:
      Object.freeze(steps),
    evidence:
      Object.freeze(evidence),
  });
}
